import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SuspensionNotice } from "@/components/auth/SuspensionNotice";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import "@/i18n";

// Critical routes loaded immediately (only Index for landing page)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Import essential providers directly (not lazy) to avoid hook issues
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { DownloadConsentProvider } from "./components/providers/DownloadConsentProvider";

// Lazy load other components
const QuickNav = lazy(() => import("./components/layout/QuickNav").then(m => ({ default: m.QuickNav })));

// Attendance system pages
const AttendanceDashboardPage = lazy(() => import("./pages/AttendanceDashboardPage"));
const StudentAttendancePage = lazy(() => import("./pages/StudentAttendancePage"));
const AttendanceSettingsPage = lazy(() => import("./pages/AttendanceSettingsPage"));

// Lazy load ALL routes 
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const VanityProfile = lazy(() => import("./pages/VanityProfile"));
const PostProject = lazy(() => import("./pages/PostProject"));
const BrowseProjects = lazy(() => import("./pages/BrowseProjects"));
const BrowseStatisticians = lazy(() => import("./pages/BrowseStatisticians"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const MyBids = lazy(() => import("./pages/MyBids"));
const ProjectManagement = lazy(() => import("./pages/ProjectManagement"));
const StatisticianWorkspace = lazy(() => import("./pages/StatisticianWorkspace"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SkillsAndCertifications = lazy(() => import("./pages/SkillsAndCertifications"));
const ReferralProgram = lazy(() => import("./pages/ReferralProgram"));
const Blog = lazy(() => import("./pages/Blog"));
const ArticleView = lazy(() => import("./pages/ArticleView"));
const ArticleCreate = lazy(() => import("./pages/ArticleCreate"));
const ClientPublicProfile = lazy(() => import("./pages/ClientPublicProfile"));
const FAQ = lazy(() => import("./pages/FAQ"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PlatformUsagePolicy = lazy(() => import("./pages/PlatformUsagePolicy"));
const ChatBot = lazy(() => import("./components/chat/ChatBot"));
const CookieConsent = lazy(() => import("./components/legal/CookieConsent"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const AdminPlatformSettings = lazy(() => import("./pages/AdminPlatformSettings"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const SecurityMonitoring = lazy(() => import("./components/admin/SecurityMonitoringDashboard").then(m => ({ default: m.SecurityMonitoringDashboard })));
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));

// Training and Marketplace Pages
const TrainingSessions = lazy(() => import("./pages/TrainingSessions"));
const TrainingSession = lazy(() => import("./pages/TrainingSession"));

const CourseDetails = lazy(() => import("./pages/CourseDetails"));
const WebinarDetails = lazy(() => import("./pages/WebinarDetails"));
const DocumentManager = lazy(() => import("./pages/DocumentManager"));
const UploadDocument = lazy(() => import("./pages/UploadDocument"));
const DocumentEdit = lazy(() => import("./pages/DocumentEdit"));
const CourseAnalytics = lazy(() => import("./pages/CourseAnalytics"));
const StudentPortal = lazy(() => import("./pages/StudentPortal"));
const LiveSession = lazy(() => import("./pages/LiveSession"));
const LiveSessionLauncher = lazy(() => import("./pages/LiveSessionLauncher"));
const CourseCreate = lazy(() => import("./pages/CourseCreate"));
const WebinarCreate = lazy(() => import("./pages/WebinarCreate"));
const CourseEdit = lazy(() => import("./pages/CourseEdit"));
const WebinarEdit = lazy(() => import("./pages/WebinarEdit"));
const Calendar = lazy(() => import("./pages/Calendar"));

const KnowledgeStore = lazy(() => import("./pages/KnowledgeStore"));

const AdvancedFeatures = lazy(() => import("./pages/AdvancedFeatures"));
const Cart = lazy(() => import("./pages/Cart"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));

// Booking and Consultation Pages
const ConsultationBookings = lazy(() => import("./pages/ConsultationBookings"));
const ConsultationSession = lazy(() => import("./pages/ConsultationSession"));
const ConsultationSuccess = lazy(() => import("./pages/ConsultationSuccess"));
const AvailabilitySettings = lazy(() => import("./pages/AvailabilitySettings"));
const ProfessionalSessionPage = lazy(() => import("./pages/ProfessionalSessionPage").then(m => ({ default: m.ProfessionalSessionPage })));
const PlatformComparison = lazy(() => import("./pages/PlatformComparison"));

// Webinar Pages
const WebinarPayment = lazy(() => import("./pages/WebinarPayment"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const TestWebinarDemo = lazy(() => import("./pages/TestWebinarDemo"));
const WebRTCTest = lazy(() => import("./pages/WebRTCTest"));
const InstantMeeting = lazy(() => import("./pages/InstantMeeting"));
const JoinMeeting = lazy(() => import("./pages/JoinMeeting"));
const MeetingRoom = lazy(() => import("./pages/MeetingRoom"));
const MyMeetings = lazy(() => import("./pages/MyMeetings"));
const MyInvitations = lazy(() => import("./pages/MyInvitations"));
const ScheduleMeeting = lazy(() => import("./pages/ScheduleMeeting"));
const MeetingTemplatesPage = lazy(() => import("./pages/MeetingTemplates"));
const RecurringMeetingsPage = lazy(() => import("./pages/RecurringMeetings"));
const MeetingAnalyticsPage = lazy(() => import("./pages/MeetingAnalytics"));
const CalendarIntegrationPage = lazy(() => import("./pages/CalendarIntegration"));

const AppContent: React.FC = () => {
  const { isSuspended } = useAuth();

  if (isSuspended) {
    return <SuspensionNotice />;
  }

  return (
    <>
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 rounded-full bg-muted mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      }>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<ArticleView />} />
          <Route path="/profile/:id" element={<PublicProfile />} />
          <Route path="/u/:username" element={<VanityProfile />} />
          <Route path="/clients/:id" element={<ClientPublicProfile />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/platform-usage" element={<PlatformUsagePolicy />} />
          <Route path="/platform-comparison" element={<PlatformComparison />} />
          
          {/* Protected routes - authentication required */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/post-project" element={<ProtectedRoute><PostProject /></ProtectedRoute>} />
          <Route path="/browse-projects" element={<ProtectedRoute><BrowseProjects /></ProtectedRoute>} />
          <Route path="/browse-statisticians" element={<ProtectedRoute><BrowseStatisticians /></ProtectedRoute>} />
          <Route path="/project/:id" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
          <Route path="/my-bids" element={<ProtectedRoute><MyBids /></ProtectedRoute>} />
          <Route path="/my-projects" element={<ProtectedRoute><ProjectManagement /></ProtectedRoute>} />
          <Route path="/workspace" element={<ProtectedRoute><StatisticianWorkspace /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/skills-certifications" element={<ProtectedRoute><SkillsAndCertifications /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><ReferralProgram /></ProtectedRoute>} />
          <Route path="/blog/create" element={<ProtectedRoute><ArticleCreate /></ProtectedRoute>} />
          
          {/* Training and Marketplace Routes */}
          <Route path="/knowledge-store" element={<KnowledgeStore />} />
          <Route path="/student-portal" element={<ProtectedRoute><StudentPortal /></ProtectedRoute>} />
          <Route path="/marketplace" element={<Navigate to="/knowledge-store" replace />} />
          <Route path="/live-session/:sessionId" element={<ProtectedRoute><LiveSession /></ProtectedRoute>} />
          <Route path="/launch-session/:courseId" element={<ProtectedRoute><LiveSessionLauncher /></ProtectedRoute>} />
          <Route path="/professional-session/:sessionId" element={<ProtectedRoute><ProfessionalSessionPage /></ProtectedRoute>} />
          <Route path="/training-courses" element={<Navigate to="/knowledge-store?tab=courses" replace />} />
          <Route path="/training-sessions" element={<ProtectedRoute><TrainingSessions /></ProtectedRoute>} />
          <Route path="/training-session/:sessionId" element={<ProtectedRoute><TrainingSession /></ProtectedRoute>} />
          
          <Route path="/course/:id" element={<CourseDetails />} />
          <Route path="/webinar/:id" element={<WebinarDetails />} />
          <Route path="/course/:id/analytics" element={<ProtectedRoute><CourseAnalytics /></ProtectedRoute>} />
          <Route path="/create-course" element={<ProtectedRoute><CourseCreate /></ProtectedRoute>} />
          <Route path="/create-webinar" element={<ProtectedRoute><WebinarCreate /></ProtectedRoute>} />
          <Route path="/course/:courseId/edit" element={<ProtectedRoute><CourseEdit /></ProtectedRoute>} />
          <Route path="/webinar/:courseId/edit" element={<ProtectedRoute><WebinarEdit /></ProtectedRoute>} />
          <Route path="/course/:courseId/attendance" element={<ProtectedRoute><AttendanceDashboardPage /></ProtectedRoute>} />
          <Route path="/course/:courseId/my-attendance" element={<ProtectedRoute><StudentAttendancePage /></ProtectedRoute>} />
          <Route path="/course/:courseId/attendance-settings" element={<ProtectedRoute><AttendanceSettingsPage /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
          <Route path="/document-marketplace" element={<Navigate to="/knowledge-store?tab=documents" replace />} />
          <Route path="/document-manager" element={<ProtectedRoute><DocumentManager /></ProtectedRoute>} />
          <Route path="/upload-document" element={<ProtectedRoute><UploadDocument /></ProtectedRoute>} />
          <Route path="/document/:id/edit" element={<ProtectedRoute><DocumentEdit /></ProtectedRoute>} />
          
          <Route path="/advanced-features" element={<ProtectedRoute><AdvancedFeatures /></ProtectedRoute>} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/orders" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
          
          {/* Booking and Consultation Routes */}
          <Route path="/consultations" element={<ProtectedRoute><ConsultationBookings /></ProtectedRoute>} />
          <Route path="/consultation-session/:bookingId" element={<ProtectedRoute><ConsultationSession /></ProtectedRoute>} />
          <Route path="/consultation-success" element={<ProtectedRoute><ConsultationSuccess /></ProtectedRoute>} />
          <Route path="/availability" element={<ProtectedRoute><AvailabilitySettings /></ProtectedRoute>} />
          
          {/* Webinar Routes - Public for guest access */}
          <Route path="/webinar-payment" element={<WebinarPayment />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/webinar-demo" element={<TestWebinarDemo />} />
          
          {/* WebRTC Test Harness - Debug tool */}
          <Route path="/webrtc-test" element={<ProtectedRoute><WebRTCTest /></ProtectedRoute>} />
          
          {/* Instant Meeting Routes */}
          <Route path="/instant-meeting" element={<ProtectedRoute><InstantMeeting /></ProtectedRoute>} />
          <Route path="/schedule-meeting" element={<ProtectedRoute><ScheduleMeeting /></ProtectedRoute>} />
          <Route path="/join-meeting" element={<JoinMeeting />} />
          <Route path="/join-meeting/:code" element={<JoinMeeting />} />
          <Route path="/meeting/:code" element={<MeetingRoom />} />
          <Route path="/my-meetings" element={<ProtectedRoute><MyMeetings /></ProtectedRoute>} />
          <Route path="/my-invitations" element={<ProtectedRoute><MyInvitations /></ProtectedRoute>} />
          <Route path="/meeting-templates" element={<ProtectedRoute><MeetingTemplatesPage /></ProtectedRoute>} />
          <Route path="/recurring-meetings" element={<ProtectedRoute><RecurringMeetingsPage /></ProtectedRoute>} />
          <Route path="/meeting-analytics" element={<ProtectedRoute><MeetingAnalyticsPage /></ProtectedRoute>} />
          <Route path="/calendar-integration" element={<ProtectedRoute><CalendarIntegrationPage /></ProtectedRoute>} />
          
          {/* Admin Routes - authentication required */}
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/content" element={<ProtectedRoute><AdminContent /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><AdminPlatformSettings /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
          <Route path="/admin/security" element={<ProtectedRoute><SecurityMonitoring /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalyticsPage /></ProtectedRoute>} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

// Create QueryClient outside of component to avoid hook issues
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <DownloadConsentProvider>
            <TooltipProvider delayDuration={0}>
              <Toaster />
              <SonnerToaster />
              <BrowserRouter>
                <AppContent />
                <Suspense fallback={null}>
                  <QuickNav />
                </Suspense>
                <Suspense fallback={null}>
                  <ChatBot />
                </Suspense>
                <Suspense fallback={null}>
                  <CookieConsent />
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </DownloadConsentProvider>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;